import { useMemo, useState } from "react";
import { Button, FormControl } from "@mui/material";
import { useAppDispatch, useAppSelector } from "../../../store/hooks";
import { addConditionRunActionExceptionRobot, addConditionRunActionRobot } from "../../../store/ses/sesRobotSlice";
import {
	addConditionRunActionDataElem,
	addConditionRunActionEndpoint,
	addConditionRunActionEndpointEntry,
	addConditionRunActionException,
} from "../../../store/ses/sesItemConfigSlice";
import useTranslate from "../../../hooks/useTranslate";
import { convertSearchDepth, createObjectWithCondition } from "../../../helpers";
import { AlertDialog } from "../../../ui";
import DraggablePopoverComponent from "../../MUI/DraggablePopoverComponent/DraggablePopoverComponent";
import ConditionBlock from "../../ConditionBlock/ConditionBlock";
import FormAddingCondition from "../../Forms/FormAddingCondition/FormAddingCondition";
import { AtomicCondition, ConditionVariantType } from "../../../types/ses/sesItemConfigTypes";
import { IRunConditionsProps } from "./RunConditions.props";
import styles from "./RunConditions.module.scss";
import { selectDataList } from "@/store/ses/sesDataElemSlice";
import { IDataElemItem } from "@/types/ses/sesDataElemTypes";
import { Virtualizer } from "@/HOC";
import { useViewportHeight } from "@/hooks";

const RunConditions = ({
	action,
	idx,
	channel,
	changeFlgHandler,
	actionFor,
	isDisabled,
	anchorElConditionsRun,
	setAnchorElConditionsRun,
	isFilterActive,
}: IRunConditionsProps): JSX.Element => {
	const [duplicatesIndexes, setDuplicatesIndexes] = useState<number[]>([]); // индексы дубликатов условий
	const [showAlertDialog, setShowAlertDialog] = useState<boolean>(false); // показ диалогового окна

	const dataElemList = useAppSelector(selectDataList); // список элементов данных
	const dataElemListOptions = useMemo<{ dataElemSlaveData: IDataElemItem[]; dataElem: IDataElemItem[] } | null>(() => {
		return dataElemList.data
			? {
					dataElemSlaveData: dataElemList.data.filter((dataElem) => dataElem.type !== "container"),
					dataElem: dataElemList.data,
			  }
			: null;
	}, [JSON.stringify(dataElemList.data)]);

	const dispatch = useAppDispatch();

	const translate = useTranslate(); // hook для перевода текста

	// обработчик добавления условия запуска действия
	const addConditionRunActionHandler = (conditionBlockIdx: number, data?: AtomicCondition | null, orBlockIndex?: number): void => {
		const conditionData: AtomicCondition = data || createObjectWithCondition("data");
		const payloadData = { channel, channelIdx: idx, conditionBlockIdx, data: conditionData };
		if (actionFor.for === "robotException") {
			dispatch(addConditionRunActionExceptionRobot({ exception: actionFor.exception, orBlockIndex: orBlockIndex, ...payloadData })); // исключения робота
			changeFlgHandler("exceptions"); // ставим флаг о несохраненных данных
		} else if (actionFor.for === "dataException") {
			dispatch(addConditionRunActionException({ targetObject: "dataElement", exception: actionFor.exception, orBlockIndex: orBlockIndex, ...payloadData })); // исключения ЭД
			changeFlgHandler("exceptions"); // ставим флаг о несохраненных данных
		} else if (actionFor.for === "endpointException") {
			dispatch(addConditionRunActionException({ targetObject: "endpoint", exception: actionFor.exception, orBlockIndex: orBlockIndex, ...payloadData })); // исключения ЭД
			changeFlgHandler("exceptions"); // ставим флаг о несохраненных данных
		} else if (actionFor.for === "endpointEntry") {
			dispatch(addConditionRunActionEndpointEntry({ ...payloadData, orBlockIndex: orBlockIndex })); // входа КТ +++
			changeFlgHandler("entry"); // ставим флаг о несохраненных данных
		} else {
			actionFor.for === "robotEvent" && dispatch(addConditionRunActionRobot({ actionEvent: actionFor.event, ...payloadData, orBlockIndex: orBlockIndex })); // события робота +++
			actionFor.for === "dataElement" && dispatch(addConditionRunActionDataElem({ actionEvent: actionFor.event, ...payloadData, orBlockIndex: orBlockIndex })); // ЭД +++
			actionFor.for === "endpoint" && dispatch(addConditionRunActionEndpoint({ ...payloadData, orBlockIndex: orBlockIndex })); // КТ +++
			changeFlgHandler("actions"); // ставим флаг о несохраненных данных
		}
	};

	// проверка на дубли условий
	const checkForDuplicateConditions = (): boolean => {
		if ("conditions" in action && Array.isArray(action.conditions) && action.conditions.length > 1) {
			const countItems: Record<string, { count: number; ids: number[] }> = {}; // промежуточный результат {json: {count, ids}}

			action.conditions.forEach((condition, idx) => {
				// меняем depth на строковый и перводит в строки
				const modifiedConditionArray = condition.map((conditionItem) => {
					if (conditionItem.type === "data" && typeof conditionItem.depth === "number")
						return JSON.stringify({ ...conditionItem, depth: convertSearchDepth(conditionItem.depth) });
					else return JSON.stringify(conditionItem);
				});
				const str = modifiedConditionArray.sort().toString();
				// если элемент уже был, то прибавляем 1, если нет - устанавливаем 1 (с добавлением индексов)
				if (countItems[str]) {
					++countItems[str].count;
					countItems[str].ids.push(idx);
				} else {
					countItems[str] = { count: 1, ids: [idx] };
				}
			});

			// обрабатываем ключи объекта, отфильтровываем все, что меньше 1
			const duplicateIds = Object.entries(countItems)
				.filter((item) => item[1].count > 1)
				.map((item) => item[1].ids)
				.flat();

			setDuplicatesIndexes(duplicateIds); // пишем индексы дубликатов

			return duplicateIds.length > 0 ? true : false;
		} else {
			setDuplicatesIndexes([]); // очищаем
			return false;
		}
	};

	// обработчик сохранения
	const saveHandler = (): void => {
		if (checkForDuplicateConditions()) setShowAlertDialog(true); // предупреждение о дубликатах
		else actionFor.onClickSaveButtonHandler(); // сохранение
	};

	// подтверждение сохранения
	const confirmSave = (): void => {
		actionFor.onClickSaveButtonHandler();
	};

	const isOrCondition = (condition: ConditionVariantType): condition is Extract<ConditionVariantType, { type: "or"; value: unknown[] }> => {
		return !!condition && condition.type === "or" && Array.isArray((condition as any).value);
	};

	const viewportHeight = useViewportHeight();

	const virtualItems = useMemo(() => {
		const conditions = "conditions" in action && Array.isArray(action.conditions) ? action.conditions : [];

		type VirtualItem = {
			type: "condition" | "separator";
			conditionDataBlock?: (typeof conditions)[number];
			conditionBlockIdx?: number;
		};

		const items: VirtualItem[] = [];

		conditions.forEach((conditionDataBlock, idx) => {
			items.push({ type: "condition", conditionDataBlock, conditionBlockIdx: idx });
			if (idx !== conditions.length - 1) items.push({ type: "separator" });
		});

		return items;
	}, [action.conditions]);

	return (
		<>
			<DraggablePopoverComponent
				anchorEl={anchorElConditionsRun}
				setAnchorEl={setAnchorElConditionsRun}
				header="title_conditionsRun"
				additionalButtons={
					<>
						{/* сохранить */}
						{actionFor.isShownSaveButton && actionFor.isAccessSaveButton && !isDisabled && (
							<FormControl fullWidth>
								<Button variant="outlined" disabled={actionFor.disabledSaveButton} sx={{ fontSize: 11 }} onClick={saveHandler}>
									{translate("button_save")}
								</Button>
							</FormControl>
						)}
					</>
				}
				// sx={{ minWidth: "500px" }}
			>
				{/* нет условий */}
				{isDisabled && virtualItems.length === 0 && <div className={styles.empty}>{translate("title_empty")}</div>}

				<Virtualizer
					items={virtualItems}
					estimateSize={120}
					overscan={1}
					style={{ minWidth: "500px", maxHeight: `${viewportHeight - 200}px`, marginInline: "-16px", paddingInline: "16px" }}
					renderItem={(item) => {
						if (item.type === "condition" && item.conditionDataBlock) {
							const condition = item.conditionDataBlock[item.conditionBlockIdx!];
							return (
								<ConditionBlock
									conditionDataBlock={item.conditionDataBlock}
									conditionBlockIdx={item.conditionBlockIdx!}
									isDisable={isDisabled}
									changeFlgHandler={changeFlgHandler}
									conditionFor={{
										for: "action",
										itsAction: actionFor.for,
										actionId: action.actionId,
										actionEvent:
											actionFor.for === "robotEvent" || actionFor.for === "dataElement"
												? actionFor.event
												: actionFor.for === "robotException" || actionFor.for === "dataException" || actionFor.for === "endpointException"
												? actionFor.exception
												: undefined,
										channel,
										channelIdx: idx,
									}}
									addConditionRunHandler={addConditionRunActionHandler}
									isWarning={duplicatesIndexes.includes(item.conditionBlockIdx!)}
									showAddCondtionBlockButton={!(isOrCondition(condition) && condition.value.length < 2)}
									dataElemListOptions={dataElemListOptions}
								/>
							);
						}

						if (item.type === "separator") {
							return (
								<div className={styles.conditionDataBlockLine}>
									<span className={styles.conditionDataBlockLineTitle}>{translate("title_or").toUpperCase()}</span>
								</div>
							);
						}

						return null;
					}}
					lastElem={
						!isDisabled &&
						!isFilterActive && (
							<FormAddingCondition
								conditionForFormVariant={!action.conditions || action.conditions.length === 0}
								addCondition={(data) => addConditionRunActionHandler(action.conditions?.length || 0, data)}
							/>
						)
					}
				/>
			</DraggablePopoverComponent>

			{/* подтверждение действия */}
			<AlertDialog
				showAlertDialog={showAlertDialog}
				setShowAlertDialog={setShowAlertDialog}
				submitHandler={confirmSave}
				title="dialog_duplicateRunConditionsFound"
				description="dialog_duplicateRunConditionsFoundConfirm"
			/>
		</>
	);
};

export default RunConditions;
